import { useRef } from "react";
import { screenOf } from "../routes/registry";
import type { Answers } from "./answers";
import { CONFIRMATION } from "./thread";

/**
 * ═══ ENTRE-T-ON DANS LE TUNNEL, OU Y CIRCULE-T-ON ? ═══
 *
 * C'est la question des gardes du parcours, et il a fallu trois formulations pour la
 * poser juste. Elle n'a jamais été « comment le document est-il arrivé » : elle est
 * « D'OÙ VIENT-ON ».
 *
 *   ENTRER    on n'était pas dans le tunnel juste avant — un lien d'e-mail, un QR code,
 *             un signet, ou le menu « Inscription » depuis l'accueil. C'est là qu'on décide
 *             où mener : au début pour qui n'a pas répondu, à sa confirmation pour qui
 *             a déjà tout donné.
 *   CIRCULER  on y était déjà — « Continuer », « Retour », le fil d'Ariane, « Modifier
 *             mon inscription ». Rediriger là fermerait les portes que le site ouvre
 *             lui-même.
 *   REPRENDRE le F5, ou l'onglet que le téléphone recharge tout seul : le document est
 *             neuf, mais l'entrée d'historique est celle où l'on était, avec ses réponses.
 *             On y circulait, on y circule encore (voir `isResumed`).
 *
 * ⚠️ TROIS CRITÈRES SONT MORTS AVANT CELUI-CI, chacun en silence, et c'est pourquoi
 * celui-ci est nommé, isolé et testé :
 *
 *   1. « RIEN DANS L'ÉTAT DE NAVIGATION ». Cet état voyage dans `history.state`, que le
 *      navigateur RESTITUE : dans un onglet ayant déjà servi, il était toujours là.
 *   2. `location.key === "default"`. Mesuré au navigateur sous React Router 7 : la clé
 *      porte une chaîne aléatoire dès le premier rendu, jamais `"default"`.
 *   3. « ON EST ENCORE SUR L'ADRESSE D'ARRIVÉE ». Vrai pour un lien d'e-mail, faux pour
 *      le cas le plus courant : sortir du tunnel vers l'accueil et y revenir par le
 *      menu. Tout y est interne, et pourtant on entre bel et bien.
 */

/** Cette adresse est-elle une étape du parcours ? */
const isStep = (path: string) => !!screenOf(path)?.funnel;

/**
 * ⚠️ `null` VEUT DIRE « ON VIENT D'OUVRIR LA PAGE », donc une entrée : il n'y a pas
 * d'écran d'avant. C'est ce qui fait qu'un lien d'e-mail et le menu « Inscription »
 * suivent la même règle sans qu'on ait à les distinguer. Sauf quand la page REPREND
 * l'entrée où l'on était (`resumed`, voir `isResumed`) : là, on circulait déjà.
 *
 * Pure, donc les quatre chemins se vérifient sans navigateur — et le dernier a coûté
 * trois tentatives.
 */
export function isEntry(
  current: string,
  previous: string | null,
  resumed = false,
): boolean {
  if (!isStep(current)) return false;
  if (previous === null) return !resumed;
  return !isStep(previous);
}

/**
 * ═══ UN RECHARGEMENT N'EST PAS UNE ENTRÉE (tâche #503) ═══
 *
 * Le F5 comptait parmi les entrées, puisqu'il n'y a pas d'écran d'avant : en pleine
 * inscription, il renvoyait à la première étape, et le `<Navigate>` partait sans état, donc
 * sans `loin`. Le fil se fermait derrière soi et il fallait recliquer « Continuer » sur
 * chaque étape déjà faite. Un téléphone qui recharge l'onglet de lui-même faisait pareil.
 *
 * ⚠️ TROIS CONDITIONS, ET CHACUNE TIENT UNE PORTE.
 *
 *   le document REPREND une entrée d'historique (rechargement, ou retour arrière que le
 *   navigateur n'avait pas gardé en mémoire) : un lien, un signet ou une adresse tapée
 *   créent une entrée neuve, et restent des entrées.
 *
 *   cette entrée porte l'ÉTAT DU PARCOURS : le shell y note `loin` à l'arrivée sur chaque
 *   étape. Sans lui, rien ne dit qu'on y circulait ; et c'est ce même état qui rend les
 *   réponses (profil, jours, billets), donc le même parcours calculé
 *   (`viewerTypo.ts` lit `answers.typoId`).
 *
 *   ⚠️ et ce n'est PAS LA CONFIRMATION : elle écrit les réponses qu'elle trouve dans cet
 *   état, et ne doit jamais le faire sur une arrivée sans « Continuer » (voir
 *   `routes/Confirmation.tsx`). Rechargée sans être inscrit, elle mène toujours au début.
 *
 * ⚠️ CE N'EST PAS LE CRITÈRE N° 1 RESSUSCITÉ. L'état restitué ne dit rien SEUL : il ne
 * compte qu'au premier écran d'un document qui reprend son entrée. Revenir de l'accueil par
 * le menu reste une entrée, puisqu'on vient d'un écran hors du tunnel.
 */
export function isResumed(
  current: string,
  restored: boolean,
  answers: Answers,
): boolean {
  return restored && current !== CONFIRMATION && Object.keys(answers).length > 0;
}

/**
 * LE DOCUMENT A-T-IL ÉTÉ OUVERT SUR UNE ENTRÉE D'HISTORIQUE QUI EXISTAIT DÉJÀ ? Lu sur la
 * navigation qui l'a chargé : `reload` pour le F5 (et l'onglet rechargé par le téléphone),
 * `back_forward` pour un retour arrière que le navigateur n'avait pas gardé en mémoire.
 * Gardé en mémoire, l'écran revient tel quel : sans ce second cas, le même geste
 * menait au début ou non selon la mémoire du téléphone.
 */
export function documentRestored(): boolean {
  try {
    const [navigation] = performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];
    return (
      navigation?.type === "reload" || navigation?.type === "back_forward"
    );
  } catch {
    /* API absente : on retombe sur la règle d'avant, qui renvoie au début */
    return false;
  }
}

/**
 * L'ADRESSE PRÉCÉDENTE — celle d'avant le dernier CHANGEMENT, et non celle du rendu
 * d'avant.
 *
 * ⚠️ LA NUANCE EST TOUT, et sans elle la garde se désarmait toute seule. Un même écran
 * se rend plusieurs fois — une requête qui répond, un état qui bouge, l'écran de
 * chargement qui s'efface. Mise à jour à chaque rendu, l'adresse « précédente » devenait
 * l'adresse COURANTE dès le second, et l'entrée n'était plus reconnue comme telle : la
 * redirection ne partait que si elle pouvait partir au tout premier rendu.
 *
 * ⚠️ MUTÉ PENDANT LE RENDU, ET C'EST VOULU : la valeur doit être juste AU RENDU où
 * l'adresse change, puisque c'est là que les gardes décident. Un effet arriverait après
 * coup — trop tard pour rendre un `<Navigate>`. C'est un verrou monotone, sans effet de
 * bord observable ailleurs.
 */
export function usePrevious(pathname: string): string | null {
  const previous = useRef<string | null>(null);
  const current = useRef(pathname);

  if (current.current !== pathname) {
    previous.current = current.current;
    current.current = pathname;
  }
  return previous.current;
}
